import { CACHE, setEdgeCache } from "@/lib/http-cache";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { pageParam } from "@/lib/search-params";
import { stripSearchParams } from "@tanstack/react-router";
import { SITE, ROBOTS_INDEX, ROBOTS_NOINDEX_FOLLOW, ROBOTS_NOINDEX } from "@/lib/site";
import { discoverImage } from "@/lib/wp";
import { getAuthorPage } from "@/lib/author-page.functions";
import { ArticleCard } from "@/components/article-card";
import { AuthorBio } from "@/components/author-bio";
import { ArticleGridSkeleton } from "@/components/article-grid-skeleton";
import { EditorialHero as StandardsHero } from "@/components/editorial-page-kit";
import { PAGINATION_ARROW_CLASS, paginationNumberClass, paginationRange } from "@/components/list-pagination";
import { categoryHeroMeta } from "@/lib/category-view";
import { fastLoad, prefetchAdjacentPages } from "@/lib/fast-load";
import { Twitter, Linkedin, Mail, ShieldCheck, UserRoundCheck, Newspaper } from "lucide-react";
import type { SearchSchemaInput } from "@tanstack/react-router";

/**
 * The archive itself is resolved server-side against a durable per-author
 * snapshot (see `author-index.server.ts`). The page never replays the byline
 * reconstruction over WordPress on the request path.
 */
const authorQuery = (slug: string, page: number) =>
  queryOptions({
    queryKey: ["author-profile", slug, page],
    queryFn: () => getAuthorPage({ data: { slug, page } }),
  });

export const Route = createFileRoute("/author/$slug")({
  validateSearch: (search: { page?: unknown } & SearchSchemaInput) => ({ page: pageParam(search.page) }),
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: async ({ params, deps, context }) => {
    const { data, cached } = await fastLoad(context.queryClient, authorQuery(params.slug, deps.page), 2600);
    if (cached && !data) throw notFound();
    await setEdgeCache(data ? CACHE.listing : CACHE.degraded);
    return { initial: data ?? null, page: deps.page };
  },

  head: ({ loaderData, params }) => {
    if (!loaderData?.initial) return { meta: [{ title: "Author not found" }, { name: "robots", content: ROBOTS_NOINDEX }] };
    const { author, expertise } = loaderData.initial;
    const name = author.name;
    const page = loaderData.page ?? 1;
    const baseUrl = `${SITE.url}/author/${params.slug}`;
    const canonical = page > 1 ? `${baseUrl}?page=${page}` : baseUrl;
    const title = page > 1
      ? `${name}, Author profile & articles (Page ${page}) | ${SITE.name}`
      : `${name}, Author profile & articles | ${SITE.name}`;
    const rawBio = author.bio.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
    const shortBio = rawBio.length > 160 ? `${rawBio.slice(0, 157)}…` : rawBio;
    const description = shortBio || `Read the latest articles, analysis and insights by ${name} on ${SITE.name}.`;
    const image = discoverImage(author.avatar, 1200);
    const sameAs = [author.twitter, author.linkedin].filter((v): v is string => Boolean(v));

    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "author", content: name },
        {
          name: "robots",
          content: page > 1 ? ROBOTS_NOINDEX_FOLLOW : ROBOTS_INDEX,
        },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "profile" },
        { property: "og:url", content: canonical },
        { property: "og:site_name", content: SITE.name },
        { property: "profile:username", content: params.slug },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(image
          ? [
              { property: "og:image", content: image },
              { property: "og:image:alt", content: `${name}, author at ${SITE.name}` },
              { name: "twitter:image", content: image },
              { name: "twitter:image:alt", content: `${name}, author at ${SITE.name}` },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            url: canonical,
            name: title,
            description,
            mainEntity: {
              "@type": "Person",
              name,
              description: rawBio || undefined,
              url: baseUrl,
              image: image || undefined,
              jobTitle: author.title || "Contributor",
              sameAs: sameAs.length ? sameAs : undefined,
              knowsAbout: expertise.length ? expertise.map((e) => e.name) : undefined,
              worksFor: { "@type": "Organization", name: SITE.name, url: SITE.url },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
              { "@type": "ListItem", position: 2, name: "Authors", item: `${SITE.url}/authors` },
              { "@type": "ListItem", position: 3, name: name, item: baseUrl },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-editorial py-24 text-center">
      <h1 className="page-title">Author not found</h1>
    </div>
  ),
  component: AuthorPage,
});

function AuthorPage() {
  const { initial } = Route.useLoaderData();
  const { page } = Route.useSearch();
  const params = Route.useParams();
  const { data } = useQuery({ ...authorQuery(params.slug, page), initialData: initial ?? undefined });
  const queryClient = useQueryClient();
  prefetchAdjacentPages(queryClient, (n) => authorQuery(params.slug, n), page, data?.posts.meta.totalPages ?? 1);
  const author = data?.author;
  const posts = data?.posts;
  const expertise = data?.expertise ?? [];
  const name = author?.name ?? params.slug.replace(/-/g, " ");
  const avatar = author?.avatar ?? undefined;
  const pageCount = posts?.meta.totalPages ?? 1;

  return (
    <>
      <StandardsHero
        metaSide
        icon={<UserRoundCheck className="h-5 w-5" />}
        title={name}
        lede={author?.title ?? `Articles, analysis and interviews by ${name}.`}
        meta={categoryHeroMeta(posts?.items ?? [], posts?.meta.total, expertise.length)}
      />

      {author ? (
        <section className="container-editorial py-10" aria-labelledby="author-bio-heading">
          <div className="grid gap-8 border border-hairline bg-card p-6 md:grid-cols-[140px_minmax(0,1fr)] md:p-8">
            <div className="flex md:block">
              {avatar ? (
                <img
                  src={avatar}
                  alt={name}
                  width={128}
                  height={128}
                  className="h-28 w-28 rounded-full border border-hairline object-cover md:h-32 md:w-32"
                  loading="eager"
                />
              ) : (
                <div className="flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-brand-deep via-brand to-brand-light font-serif text-3xl font-bold text-white md:h-32 md:w-32">
                  {name.slice(0, 1).toUpperCase()}
                </div>
              )}
            </div>

            <div>
              <h2 id="author-bio-heading" className="sr-only">
                About {name}
              </h2>
              <AuthorBio
                variant="full"
                text={author.bio}
                authorName={name}
                authorSlug={params.slug}
                authorTitle={author.title}
                location="author-page"
              />

              {expertise.length ? (
                <div className="mt-5 flex flex-wrap gap-2">
                  {expertise.map((e) => (
                    <Link
                      key={e.slug}
                      to="/$slug"
                      params={{ slug: e.slug }}
                      className="rounded-full border border-hairline px-3 py-1 font-sans text-[10px] font-semibold uppercase tracking-wide text-ink-muted hover:border-brand hover:text-brand"
                    >
                      {e.name}
                    </Link>
                  ))}
                </div>
              ) : null}

              {author.twitter || author.linkedin || author.email ? (
                <div className="mt-5 flex flex-wrap items-center gap-5">
                  {author.twitter ? (
                    <a
                      href={author.twitter}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-ink-muted hover:text-brand"
                    >
                      <Twitter className="h-3.5 w-3.5 text-brand" /> Twitter
                    </a>
                  ) : null}
                  {author.linkedin ? (
                    <a
                      href={author.linkedin}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="inline-flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-ink-muted hover:text-brand"
                    >
                      <Linkedin className="h-3.5 w-3.5 text-brand" /> LinkedIn
                    </a>
                  ) : null}
                  {author.email ? (
                    <a
                      href={`mailto:${author.email}`}
                      className="inline-flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-ink-muted hover:text-brand"
                    >
                      <Mail className="h-3.5 w-3.5 text-brand" /> Email
                    </a>
                  ) : null}
                </div>
              ) : null}

              <p className="mt-6 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-ink-muted">
                <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-brand" />
                Every story is verified against our{" "}
                <Link to="/editorial-standards" className="font-semibold text-brand hover:underline">
                  editorial standards
                </Link>
                . See our{" "}
                <Link to="/corrections-policy" className="font-semibold text-brand hover:underline">
                  corrections policy
                </Link>
                .
              </p>
            </div>
          </div>
        </section>
      ) : null}

      {!posts ? (
        <ArticleGridSkeleton />
      ) : (
        <section className="container-editorial pb-10" aria-labelledby="author-stories-heading">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4 border-t border-hairline pt-6">
            <div>
              <p className="category-eyebrow">
                <span className="text-brand" aria-hidden="true">
                  <Newspaper className="h-3 w-3" />
                </span>
                Archive
              </p>
              <h2 id="author-stories-heading" className="mt-2 font-serif text-2xl font-bold leading-tight md:text-3xl">
                Stories by {name}
              </h2>
              <div className="mt-3 h-1 w-20 rounded-full bg-gradient-to-r from-brand-deep via-brand to-brand-light" />
            </div>
            {posts.meta.total ? (
              <p className="font-sans text-[11px] uppercase tracking-wide text-ink-muted">
                {posts.meta.total.toLocaleString("en-US")} stories · Page {page} of {pageCount}
              </p>
            ) : null}
          </div>

          {posts.items.length ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {posts.items.map((p) => (
                <ArticleCard key={p.id} post={p} variant="lead" headingLevel={3} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-muted">No published articles yet.</p>
          )}
        </section>
      )}

      {pageCount > 1 ? (
        <nav
          aria-label="Pagination"
          className="container-editorial flex flex-wrap items-center justify-center gap-2 pb-16 pt-6 hairline-t"
        >
          {page > 1 ? (
            <Link to="/author/$slug" params={{ slug: params.slug }} search={{ page: page - 1 }} className={PAGINATION_ARROW_CLASS}>
              Previous
            </Link>
          ) : (
            <span className={`${PAGINATION_ARROW_CLASS} pointer-events-none opacity-40`}>Previous</span>
          )}
          {paginationRange(page, pageCount).map((item, i) =>
            item === "gap" ? (
              <span key={`gap-${i}`} className="px-1 font-mono text-xs text-ink-muted">
                …
              </span>
            ) : (
              <Link
                key={item}
                to="/author/$slug"
                params={{ slug: params.slug }}
                search={{ page: item }}
                aria-current={item === page ? "page" : undefined}
                className={paginationNumberClass(item === page)}
              >
                {item}
              </Link>
            ),
          )}
          {page < pageCount ? (
            <Link to="/author/$slug" params={{ slug: params.slug }} search={{ page: page + 1 }} className={PAGINATION_ARROW_CLASS}>
              Next
            </Link>
          ) : (
            <span className={`${PAGINATION_ARROW_CLASS} pointer-events-none opacity-40`}>Next</span>
          )}
        </nav>
      ) : null}
    </>
  );
}
